import type { EChartsOption } from 'echarts';
import {
  u340xEchartsPlotInset,
  u340xEchartsTheme,
} from '../theme/u340xEchartsTheme';
import type { U340xKpiMetricConfig, U340xKpiSeriesPoint } from '../types/u340xKpiTypes';

export interface U340xBuildDualAxisOptionParams {
  series: U340xKpiSeriesPoint[];
  primaryMetric: U340xKpiMetricConfig & { axis: 'left' };
  secondaryMetric: U340xKpiMetricConfig & { axis: 'right' };
  /** Barre empilée (heures non facturables / write-off count). */
  primaryStackMetric?: U340xKpiMetricConfig & { axis: 'left' };
  /** Ligne $ supplémentaire (non facturable / write-off HT). */
  secondaryExtraMetric?: U340xKpiMetricConfig & { axis: 'right' };
  /** Ligne TEC STANDARD (U3403). */
  secondaryTecMetric?: U340xKpiMetricConfig & { axis: 'right' };
  /** Ligne TEC write-off (U3403). */
  secondaryTecExtraMetric?: U340xKpiMetricConfig & { axis: 'right' };
  /** Mois sélectionné (`yyyy-MM`) — barre surlignée. */
  selectedMonth?: string | null;
}

function toNumber(value: string | number | undefined): number {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
}

function formatAxisValue(value: number, format?: 'currency' | 'number'): string {
  if (format === 'currency') {
    return new Intl.NumberFormat('fr-CA', {
      style: 'currency',
      currency: 'CAD',
      maximumFractionDigits: 0,
    }).format(value);
  }
  return new Intl.NumberFormat('fr-CA', { maximumFractionDigits: 1 }).format(value);
}

function buildBarData(
  values: number[],
  series: U340xKpiSeriesPoint[],
  selectedMonth: string | null | undefined,
  highlightColor: string,
): number[] | Array<{ value: number; itemStyle?: { color: string } }> {
  if (selectedMonth == null || selectedMonth === '') {
    return values;
  }
  return values.map((value, index) => ({
    value,
    itemStyle:
      series[index]?.month === selectedMonth ? { color: highlightColor } : undefined,
  }));
}

function buildLineData(values: number[], borderColor: string) {
  return values.map((value) => ({
    value,
    symbol: 'circle' as const,
    symbolSize: 10,
    itemStyle: {
      color: '#ffffff',
      borderColor,
      borderWidth: 2,
    },
  }));
}

type LineDash = 'solid' | 'dashed' | 'dotted';

function pushRightAxisLine(
  chartSeries: any[],
  metric: U340xKpiMetricConfig & { axis: 'right' },
  values: number[],
  lineColor: string,
  dash: LineDash,
) {
  chartSeries.push({
    name: metric.label,
    type: 'line',
    yAxisIndex: 1,
    data: buildLineData(values, lineColor),
    smooth: true,
    showSymbol: true,
    showAllSymbol: true,
    symbol: 'circle',
    symbolSize: 10,
    lineStyle: {
      width: 2,
      color: lineColor,
      type: dash === 'solid' ? undefined : dash,
    },
    itemStyle: {
      color: '#ffffff',
      borderColor: lineColor,
      borderWidth: 2,
    },
    clip: false,
    z: 10,
    zlevel: 1,
  });
}

/**
 * Construit une option ECharts dual-axe : barre(s) gauche + ligne(s) droite.
 * U3402 : barres empilées heures + 2 lignes $.
 * U3403 : barres empilées count + jusqu’à 4 lignes $ (HT + TEC × STANDARD/WO).
 */
export function u340xBuildDualAxisOption(params: U340xBuildDualAxisOptionParams): EChartsOption {
  const {
    series,
    primaryMetric,
    secondaryMetric,
    primaryStackMetric,
    secondaryExtraMetric,
    secondaryTecMetric,
    secondaryTecExtraMetric,
    selectedMonth,
  } = params;
  const categories = series.map((point) => point.month);
  const primaryValues = series.map((point) => toNumber(point[primaryMetric.key]));
  const secondaryValues = series.map((point) => toNumber(point[secondaryMetric.key]));
  const stackValues = primaryStackMetric
    ? series.map((point) => toNumber(point[primaryStackMetric.key]))
    : null;
  const extraValues = secondaryExtraMetric
    ? series.map((point) => toNumber(point[secondaryExtraMetric.key]))
    : null;
  const tecValues = secondaryTecMetric
    ? series.map((point) => toNumber(point[secondaryTecMetric.key]))
    : null;
  const tecExtraValues = secondaryTecExtraMetric
    ? series.map((point) => toNumber(point[secondaryTecExtraMetric.key]))
    : null;

  const hasStack = Boolean(primaryStackMetric && stackValues);
  const barHighlight = hasStack ? '#1e40af' : '#1d4ed8';
  const stackHighlight = '#60a5fa';
  const lineColor = u340xEchartsTheme.color[1];
  const extraLineColor = u340xEchartsTheme.color[3];
  const tecLineColor = u340xEchartsTheme.color[4];
  const tecExtraLineColor = u340xEchartsTheme.color[5];
  const stackBarColor = u340xEchartsTheme.color[2];

  const legendData = [
    primaryMetric.label,
    ...(primaryStackMetric ? [primaryStackMetric.label] : []),
    secondaryMetric.label,
    ...(secondaryExtraMetric ? [secondaryExtraMetric.label] : []),
    ...(secondaryTecMetric ? [secondaryTecMetric.label] : []),
    ...(secondaryTecExtraMetric ? [secondaryTecExtraMetric.label] : []),
  ];

  const chartSeries: NonNullable<EChartsOption['series']> = [
    {
      name: primaryMetric.label,
      type: 'bar',
      yAxisIndex: 0,
      stack: hasStack ? 'hours' : undefined,
      data: buildBarData(primaryValues, series, selectedMonth, barHighlight),
      barMaxWidth: 28,
      itemStyle: { color: u340xEchartsTheme.color[0] },
      z: 1,
    },
  ];

  if (primaryStackMetric && stackValues) {
    chartSeries.push({
      name: primaryStackMetric.label,
      type: 'bar',
      yAxisIndex: 0,
      stack: 'hours',
      data: buildBarData(stackValues, series, selectedMonth, stackHighlight),
      barMaxWidth: 28,
      itemStyle: { color: stackBarColor },
      z: 1,
    });
  }

  pushRightAxisLine(chartSeries, secondaryMetric, secondaryValues, lineColor, 'solid');

  if (secondaryExtraMetric && extraValues) {
    pushRightAxisLine(chartSeries, secondaryExtraMetric, extraValues, extraLineColor, 'dashed');
  }
  if (secondaryTecMetric && tecValues) {
    pushRightAxisLine(chartSeries, secondaryTecMetric, tecValues, tecLineColor, 'solid');
  }
  if (secondaryTecExtraMetric && tecExtraValues) {
    pushRightAxisLine(
      chartSeries,
      secondaryTecExtraMetric,
      tecExtraValues,
      tecExtraLineColor,
      'dotted',
    );
  }

  const hasManyLegendItems = legendData.length > 4;
  const gridBottom = hasManyLegendItems
    ? u340xEchartsPlotInset.bottomPx + 20
    : u340xEchartsPlotInset.bottomPx;

  return {
    color: [...u340xEchartsTheme.color],
    textStyle: {
      fontFamily: u340xEchartsTheme.fontFamily,
      color: u340xEchartsTheme.textColor,
    },
    tooltip: {
      trigger: 'axis',
      backgroundColor: u340xEchartsTheme.tooltipBg,
      borderColor: u340xEchartsTheme.tooltipBorder,
      textStyle: { color: u340xEchartsTheme.textColor },
      valueFormatter: (value) => {
        const num = typeof value === 'number' ? value : Number(value);
        return formatAxisValue(Number.isFinite(num) ? num : 0);
      },
    },
    legend: {
      data: legendData,
      bottom: 0,
      textStyle: { color: u340xEchartsTheme.mutedTextColor },
    },
    grid: {
      left: u340xEchartsPlotInset.leftPx,
      right: u340xEchartsPlotInset.rightPx,
      top: u340xEchartsPlotInset.topPx,
      bottom: gridBottom,
      containLabel: false,
    },
    xAxis: {
      type: 'category',
      data: categories,
      axisLine: { lineStyle: { color: u340xEchartsTheme.axisLineColor } },
      axisLabel: { color: u340xEchartsTheme.mutedTextColor },
    },
    yAxis: [
      {
        type: 'value',
        name: primaryMetric.label,
        nameTextStyle: { color: u340xEchartsTheme.mutedTextColor, align: 'left' },
        axisLine: { show: false },
        splitLine: { lineStyle: { color: u340xEchartsTheme.splitLineColor } },
        axisLabel: {
          color: u340xEchartsTheme.mutedTextColor,
          hideOverlap: true,
          formatter: (value: number) => formatAxisValue(value, primaryMetric.format ?? 'number'),
        },
      },
      {
        type: 'value',
        name: secondaryMetric.label,
        nameTextStyle: { color: u340xEchartsTheme.mutedTextColor, align: 'right' },
        axisLine: { show: false },
        splitLine: { show: false },
        axisLabel: {
          color: u340xEchartsTheme.mutedTextColor,
          hideOverlap: true,
          formatter: (value: number) => formatAxisValue(value, secondaryMetric.format ?? 'currency'),
        },
      },
    ],
    series: chartSeries,
  };
}
